import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Header, Footer } from "@/components/Layout";
import { useSeo } from "@/hooks/useSeo";
import { Loader2, Star, ArrowRight, BookOpen, Search } from "lucide-react";
import { getGuides } from "@/lib/api";
import { isGeneral, flagProps } from "@/lib/countries";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "";

const CardFlag = ({ category }) => {
  const f = flagProps(category);
  if (!f) return null;
  return (
    <img
      {...f}
      width={20}
      height={15}
      alt={`${category} flag`}
      loading="lazy"
      className="rounded-[2px] shadow-sm shrink-0"
      data-testid="guide-flag"
    />
  );
};

const ArticleCard = ({ a, featured }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
  >
    <Link
      to={`/guides/${a.slug}`}
      data-testid={`guide-card-${a.slug}`}
      className={`block h-full rounded-2xl border bg-white p-6 transition-colors hover:border-clay/50 ${
        featured ? "border-clay/30" : "border-border"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        {featured ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-clay">
            <Star className="h-3 w-3 fill-clay" /> Featured
          </span>
        ) : (
          <span />
        )}
        {!isGeneral(a.category) && (
          <span className="inline-flex items-center gap-1.5 text-xs text-ink-muted">
            <CardFlag category={a.category} />
            {a.category}
          </span>
        )}
      </div>
      <h3 className="mt-2 font-heading text-xl text-ink leading-snug">{a.title}</h3>
      {a.excerpt && <p className="mt-2 text-ink-soft leading-relaxed">{a.excerpt}</p>}
      <div className="mt-3 text-xs text-ink-faint">{fmtDate(a.created_at)}</div>
    </Link>
  </motion.div>
);

export default function Guides() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState("All");

  useSeo({
    title: "Expert guides",
    description:
      "Expert guides from graduates and tutors at leading UK universities on writing a standout UCAS personal statement as an international applicant.",
  });

  useEffect(() => {
    getGuides()
      .then(setData)
      .catch(() => setData({ featured: [], articles: [] }))
      .finally(() => setLoading(false));
  }, []);

  const articles = useMemo(() => data?.articles || [], [data]);

  // Category options: only "General" / countries that actually have guides.
  const catOptions = useMemo(() => {
    const cats = new Set(articles.map((a) => a.category || "General"));
    const hasGeneral = [...cats].some((c) => isGeneral(c));
    const countries = [...cats].filter((c) => !isGeneral(c)).sort((a, b) => a.localeCompare(b));
    return ["All", ...(hasGeneral ? ["General"] : []), ...countries];
  }, [articles]);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      articles.filter((a) => {
        const cat = a.category || "General";
        const catOk =
          activeCat === "All" ||
          (activeCat === "General" ? isGeneral(cat) : cat === activeCat);
        const text = `${a.title} ${a.excerpt || ""}`.toLowerCase();
        return catOk && (!q || text.includes(q));
      }),
    [articles, activeCat, q]
  );

  const isFiltering = activeCat !== "All" || q.length > 0;

  return (
    <div className="min-h-screen flex flex-col bg-sand">
      <Header />
      <main className="flex-1 max-w-4xl mx-auto w-full px-6 sm:px-8 py-10 sm:py-14">
        <div className="text-center">
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-ink">Expert guides</h1>
          <p className="mt-3 text-ink-soft max-w-2xl mx-auto leading-relaxed">
            Practical advice from graduates and tutors at leading UK universities — written to help
            international applicants write a statement that stands out.
          </p>
        </div>

        {loading ? (
          <div className="grid place-items-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-clay" />
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-20" data-testid="guides-empty">
            <BookOpen className="h-10 w-10 text-ink-faint mx-auto" />
            <p className="mt-4 text-ink-muted">No guides published yet — check back soon.</p>
          </div>
        ) : (
          <>
            {/* Search + category filter */}
            <div className="mt-10 space-y-4">
              <div className="relative max-w-md mx-auto">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-faint" />
                <input
                  data-testid="guides-search"
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search guides…"
                  className="w-full bg-white border border-border focus:border-clay focus:ring-1 focus:ring-clay rounded-full pl-11 pr-4 py-3 text-sm outline-none"
                />
              </div>
              {catOptions.length > 1 && (
                <div className="flex flex-wrap justify-center gap-2" data-testid="guides-filter">
                  {catOptions.map((cat) => {
                    const label = cat === "General" ? "General guides" : cat;
                    const active = activeCat === cat;
                    return (
                      <button
                        key={cat}
                        data-testid={`guides-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => setActiveCat(cat)}
                        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm transition-colors ${
                          active ? "bg-clay text-white" : "bg-white border border-border text-ink-soft hover:border-clay/50"
                        }`}
                      >
                        {!isGeneral(cat) && cat !== "All" && <CardFlag category={cat} />}
                        {label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Featured + CTA only when browsing the full, unfiltered list */}
            {!isFiltering && data.featured.length > 0 && (
              <section className="mt-12" data-testid="guides-featured">
                <h2 className="font-heading text-sm uppercase tracking-[0.2em] text-clay">Featured</h2>
                <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {data.featured.map((a) => (
                    <ArticleCard key={a.slug} a={a} featured />
                  ))}
                </div>
              </section>
            )}

            {!isFiltering && (
              <div className="mt-10 text-center">
                <Link to="/analyse" data-testid="guides-cta" className="inline-flex items-center gap-2 rounded-lg bg-clay hover:bg-clay-hover text-white px-7 py-4 font-medium transition-colors">
                  Get instant feedback on your statement
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}

            <section className="mt-12" data-testid="guides-all">
              <h2 className="font-heading text-sm uppercase tracking-[0.2em] text-ink-muted">
                {isFiltering ? `Results (${filtered.length})` : "All guides"}
              </h2>
              {filtered.length === 0 ? (
                <p className="mt-4 text-ink-muted" data-testid="guides-no-results">
                  No guides match your search. Try a different term or category.
                </p>
              ) : (
                <div className="mt-4 grid gap-5 sm:grid-cols-2">
                  {filtered.map((a) => (
                    <ArticleCard key={a.slug} a={a} />
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
